// hooks/useSSEStream.ts
import config from '../config';
import { useState, useCallback, useRef, useEffect } from 'react';
import { acquireCustomApiToken, acquireGraphToken } from '../config/msalConfig';

interface ChunkData {
	text?: string;
	response?: string;
	message?: string;
	status?: string;
}

interface Chunk {
	index: number;
	type: 'delta' | 'complete' | 'close' | 'toolusage' | 'status';
	data: ChunkData;
	timestamp: string;
}

interface StreamResponse {
	sessionId: string;
	conversationId?: string;
	status: 'initializing' | 'streaming' | 'completed' | 'error';
	chunks: Array<Chunk>;
	hasMore: boolean;
	nextChunkIndex: number;
	error: string | null;
	finalResponse: string | null;
	metadata?: {
		conversationId?: string;
		[key: string]: any;
	};
}

// Helper function to decode unicode and normalize text
const decodeStreamText = (text: string): string => {
	return text
		.replace(/\\u([0-9a-fA-F]{4})/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
		.replace(/\\r\\n/g, '\n')
		.replace(/\\r/g, '\n')
		.replace(/\\n/g, '\n');
};

export function useSSEStream() {
	const [isLoading, setIsLoading] = useState(false);
	const [streamedText, setStreamedText] = useState('');
	const [statusMessage, setStatusMessage] = useState('');
	const [isToolUsage, setIsToolUsage] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const chunkIndexRef = useRef<number>(0);
	const processedChunksRef = useRef<Set<number>>(new Set());
	const currentSessionIdRef = useRef<string | null>(null);
	const conversationIdRef = useRef<string | null>(null);
	const pollingInProgressRef = useRef<boolean>(false);

	const [authToken, setAuthToken] = useState<string | null>(null);
	const [graphToken, setGraphToken] = useState<string | null>(null);

	useEffect(() => {
		(async () => {
			try {
				const custom = await acquireCustomApiToken();

				setAuthToken(custom?.accessToken ?? null);

				const graph = await acquireGraphToken();
				setGraphToken(graph?.accessToken ?? null);
			} catch (e) {
				console.error('Error acquiring tokens', e);
			}
		})();
	}, []);

	const pollForChunks = useCallback(
		async (sessionId: string) => {
			// Prevent concurrent polling
			if (pollingInProgressRef.current) {
				return true; // Continue polling
			}

			pollingInProgressRef.current = true;

			try {
				const currentChunkIndex = chunkIndexRef.current;
				const url = `${config.STREAMING_API_URL}/grants-chat/durable-enhanced/stream/poll/${sessionId}/${currentChunkIndex}?code=${config.STREAMING_API_KEY}`;
				// streaming issues proxy through api `${config.APIM_BASE_URL}/ai/grants-chat/durable-enhanced/stream/poll/${sessionId}/${currentChunkIndex}`;

				const response = await fetch(url, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						// 'Ocp-Apim-Subscription-Key': config.APIM_KEY,
						...(authToken && { Authorization: `Bearer ${authToken}` }),
					},
					signal: abortControllerRef.current?.signal,
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const data: StreamResponse = await response.json();

				// Extract conversationId from response if available
				if (data.conversationId) {
					conversationIdRef.current = data.conversationId;
				} else if (data.metadata?.conversationId) {
					conversationIdRef.current = data.metadata.conversationId;
				}

				// Update streamed text with new chunks
				if (data.chunks && data.chunks.length > 0) {
					let newText = '';
					let newStatusMessage = '';
					let hasCompleteResponse = false;
					let completeResponseText = '';
					let hasToolUsage = false;

					for (const chunk of data.chunks) {
						// Only process chunks we haven't seen before
						if (!processedChunksRef.current.has(chunk.index)) {
							processedChunksRef.current.add(chunk.index);

							if (chunk.type === 'delta' && chunk.data.text) {
								newText += decodeStreamText(chunk.data.text);
							} else if (chunk.type === 'complete' && chunk.data.response) {
								// Handle complete response - this should replace all previous delta chunks
								hasCompleteResponse = true;
								completeResponseText = decodeStreamText(chunk.data.response);
							} else if (chunk.type === 'toolusage' && chunk.data.text) {
								// Handle tool usage chunks - replace status message (don't append)
								newStatusMessage = decodeStreamText(chunk.data.text);
								hasToolUsage = true;
							} else if (chunk.type === 'status' && chunk.data.message) {
								// Handle status chunks (processing, toolusage, streaming) - replace status message (don't append)
								newStatusMessage = decodeStreamText(chunk.data.message);
								// Check if this is a tool usage status
								hasToolUsage = chunk.data.status === 'toolusage';
							}
						}
					}

					// If we have a complete response, replace all previous text
					if (hasCompleteResponse) {
						setStreamedText(completeResponseText);
					} else if (newText) {
						// Otherwise, append new delta text progressively
						setStreamedText(prev => prev + newText);
					}

					// Update status message (replace, don't append, for cleaner display)
					if (newStatusMessage) {
						setStatusMessage(newStatusMessage);
					}

					// Update tool usage state
					setIsToolUsage(hasToolUsage);

					// Update chunk index to the next expected chunk
					// Only advance if we received chunks starting from our current index
					if (data.chunks.some(chunk => chunk.index === chunkIndexRef.current)) {
						// Find the highest consecutive chunk index we have
						let nextExpectedIndex = chunkIndexRef.current;
						const sortedChunks = data.chunks.sort((a, b) => a.index - b.index);

						for (const chunk of sortedChunks) {
							if (chunk.index === nextExpectedIndex) {
								nextExpectedIndex++;
							} else if (chunk.index > nextExpectedIndex) {
								// Gap found - don't advance beyond the gap
								break;
							}
						}

						chunkIndexRef.current = nextExpectedIndex;
					} else {
						console.warn(data.chunks.map(c => c.index));
					}
				}

				// Handle different statuses
				if (data.status === 'completed') {
					// Stream is complete - the final text is already set from either delta chunks or complete response
					setIsLoading(false);
					if (pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
				} else if (data.status === 'error') {
					setError(data.error || 'Stream error occurred');
					setIsLoading(false);
					if (pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
				} else if (data.hasMore) {
					// Continue polling if there are more chunks
					return true;
				} else {
					// No more chunks, but not completed yet - keep polling
					return true;
				}

				return false; // Stop polling
			} catch (err: any) {
				if (err.name !== 'AbortError') {
					setError(err instanceof Error ? err.message : 'Unknown error');
					setIsLoading(false);
					if (pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
				}
				return false;
			} finally {
				pollingInProgressRef.current = false;
			}
		},
		[authToken],
	);

	const startStream = useCallback(
		async (chatContent: Array<any>, model?: string, provider?: string, conversationId?: string) => {
			setIsLoading(true);
			setError(null);
			setStreamedText('');
			setStatusMessage('');
			setIsToolUsage(false);

			// Reset chunk index and processed chunks for new stream
			chunkIndexRef.current = 0;
			processedChunksRef.current.clear();
			pollingInProgressRef.current = false;
			conversationIdRef.current = conversationId || null;

			// Cancel previous stream if running
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}

			abortControllerRef.current = new AbortController();

			// Build URL with query parameters
			const urlParams = new URLSearchParams({
				code: config.STREAMING_API_KEY,
			});

			// Add model and provider as URL parameters if provided
			if (provider) {
				urlParams.append('chat-provider', provider);
			}
			if (model) {
				urlParams.append('chat-model', model);
			}

			const url = `${config.STREAMING_API_URL}/grants-chat/durable-enhanced/stream/init?${urlParams.toString()}`; // `${config.APIM_BASE_URL}/ai/grants-chat/durable-enhanced/stream/init`;

			try {
				// Initialize the stream
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Ocp-Apim-Subscription-Key': config.APIM_KEY,
						...(graphToken && { 'Graph-Authorization': `Bearer ${graphToken}` }),
						...(authToken && { Authorization: `Bearer ${authToken}` }),
					},
					body: JSON.stringify({
						chatContent: chatContent,
						conversationId: conversationId,
						timestamp: Date.now(),
					}),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const data: StreamResponse = await response.json();

				if (data.error) {
					throw new Error(data.error);
				}

				if (!data.sessionId) {
					throw new Error('No session ID received from server');
				}

				// Store session ID for potential cancellation
				currentSessionIdRef.current = data.sessionId;

				// Start polling for chunks
				pollingIntervalRef.current = setInterval(async () => {
					const shouldContinue = await pollForChunks(data.sessionId);
					if (!shouldContinue && pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
				}, 200); // Poll every 200ms for faster chunk retrieval

				// Do an initial poll immediately
				const shouldContinue = await pollForChunks(data.sessionId);
				if (!shouldContinue && pollingIntervalRef.current) {
					clearInterval(pollingIntervalRef.current);
					pollingIntervalRef.current = null;
				}

				// Return the sessionId to the caller so it can be used as conversationId
				return data.sessionId;
			} catch (err: any) {
				if (err.name !== 'AbortError') {
					setError(err instanceof Error ? err.message : 'Unknown error');
				}
				setIsLoading(false);
				return undefined;
			}
		},
		[pollForChunks, authToken, graphToken],
	);

	const cancelStream = useCallback(async () => {
		const sessionId = currentSessionIdRef.current;
		if (!sessionId) {
			console.warn('No session ID available for cancellation');
			return;
		}

		const url = `${config.STREAMING_API_URL}/grants-chat/durable/stream/cancel/${sessionId}?code=${config.STREAMING_API_KEY}`; // `${config.APIM_BASE_URL}/ai/grants-chat/durable/stream/cancel/${sessionId}`;

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Ocp-Apim-Subscription-Key': config.APIM_KEY,
					...(authToken && { Authorization: `Bearer ${authToken}` }),
				},
			});

			if (!response.ok) {
				console.error(`Cancel failed: ${response.status} - ${response.statusText}`);
			}
		} catch (error) {
			console.error('Cancel error:', error);
		}

		// Always clean up local state regardless of API call result
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		currentSessionIdRef.current = null;
		setIsLoading(false);
	}, [authToken]);

	const stopStream = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		currentSessionIdRef.current = null;
		setIsLoading(false);
	}, []);

	// Cleanup when component unmounts
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
			}
		};
	}, []);

	return {
		startStream,
		stopStream,
		cancelStream,
		isLoading,
		streamedText,
		statusMessage,
		isToolUsage,
		error,
		conversationId: conversationIdRef.current,
	};
}
